// Topic-matched calls to action for blog posts: the "Free tool" card beside the title and the
// closing CTA that names the matching service page. Plain JS so the node tests can check the mapping.
// Tags are the ones posts carry in content/blog/*.md frontmatter and in src/blog/legacyPosts.ts.

const TOOLS = {
  retention: { href: '/tools/retention-value-calculator', label: 'Open the retention value calculator', question: 'What is a better repeat rate worth to you?', body: 'See what a better 90-day repeat rate is worth in revenue and gross profit, and what each point is worth.' },
  stackQuiz: { href: '/tools/ecommerce-data-stack-quiz', label: 'Build your data stack', question: 'Which ecommerce data stack fits your team?', body: 'Ten questions about channels, team, and reporting today. Get one pick per layer, from ingestion to BI.' },
  analyticsQuiz: { href: '/tools/ecommerce-analytics-tool-quiz', label: 'Take the analytics setup quiz', question: 'Which ecommerce analytics setup fits you?', body: 'Eight questions about your channels, team, and budget. Get a recommended path, from Shopify reports to a warehouse build, with the reasons.' },
  powerbiAssessment: { href: '/tools/power-bi-to-sigma-migration-assessment', label: 'Start the assessment', question: 'How big is your Power BI to Sigma move?', body: 'Describe your Power BI estate and get a readout: size, what needs rebuilding, a pilot pick, and a phase plan.' },
  tableauAssessment: { href: '/tools/tableau-to-sigma-migration-assessment', label: 'Start the assessment', question: 'How big is your Tableau to Sigma move?', body: 'Describe your Tableau estate and get a readout: size, what needs rebuilding, a pilot pick, and a phase plan.' },
  sigmaAssessment: {
    href: '/tools/tableau-to-sigma-migration-assessment',
    label: 'Coming from Tableau',
    question: 'How big is your move to Sigma?',
    body: 'Describe your current BI estate and get a readout: size, what needs rebuilding, a pilot pick, and a phase plan.',
    alt: { href: '/tools/power-bi-to-sigma-migration-assessment', label: 'Coming from Power BI' },
  },
  tableauTranslator: { href: '/tools/tableau-to-sigma-formula-translator', label: 'Translate a formula', question: 'Have a Tableau formula to rebuild in Sigma?', body: 'Paste a calculated field and get the Sigma formula, with a plan for LOD expressions and table calculations.' },
  daxTranslator: { href: '/tools/dax-to-sigma-formula-translator', label: 'Translate a measure', question: 'Have a DAX measure to rebuild in Sigma?', body: 'Paste a DAX measure and get the Sigma formula, with a plan for CALCULATE, filter context, and time intelligence.' },
  biQuiz: { href: '/tools/bi-tool-quiz', label: 'Take the BI tool quiz', question: 'Which BI tool fits your team?', body: 'Eight questions about your warehouse, report builders, and budget. Get a recommendation with the reasons.' },
  reportingCalculator: { href: '/tools/reporting-time-calculator', label: 'Open the calculator', question: 'How much time do manual reports cost you?', body: 'Estimate the hours and cost of building and reconciling reports by hand, and how long automating them would take to pay back.' },
};

const has = (tags, ...wanted) => wanted.some((tag) => tags.includes(tag));

/** The free tool shown beside the title: the closest match to the post's tags. */
export function toolForTags(tags) {
  if (has(tags, 'formulas') && has(tags, 'powerbi')) return TOOLS.daxTranslator;
  if (has(tags, 'formulas') && has(tags, 'tableau')) return TOOLS.tableauTranslator;
  if (has(tags, 'ecommerce')) {
    if (has(tags, 'retention')) return TOOLS.retention;
    // Reporting and marketing questions fit the setup quiz; data plumbing fits the stack quiz.
    if (has(tags, 'analytics', 'marketing', 'reporting', 'decision-making') && !has(tags, 'data-modeling', 'klaviyo', 'apis')) return TOOLS.analyticsQuiz;
    return TOOLS.stackQuiz;
  }
  if (has(tags, 'sigma')) {
    if (has(tags, 'powerbi') && !has(tags, 'tableau')) return TOOLS.powerbiAssessment;
    if (has(tags, 'tableau') && !has(tags, 'powerbi')) return TOOLS.tableauAssessment;
    if (has(tags, 'migration', 'consulting')) return TOOLS.sigmaAssessment;
    if (has(tags, 'excel')) return TOOLS.reportingCalculator;
    return TOOLS.tableauTranslator;
  }
  if (has(tags, 'powerbi', 'tableau', 'bi')) return TOOLS.biQuiz;
  return TOOLS.reportingCalculator;
}

/** The closing CTA. The first link is the matching service page. */
export function nextStepForTags(tags) {
  if (has(tags, 'ecommerce')) {
    const paper = has(tags, 'retention')
      ? { href: '/white-papers/customer-retention-reporting', label: 'Get the retention reporting white paper' }
      : { href: '/white-papers/ecommerce-data-stack', label: 'Get the data stack white paper' };
    return {
      title: 'Need your ecommerce reports to agree?',
      body: 'Bring the reports that disagree and the decision they need to support. We can review the sources, definitions, and gaps together.',
      booking: 'Discuss your reporting problem',
      links: [{ href: '/ecommerce', label: 'Explore ecommerce reporting help' }, paper],
    };
  }
  if (has(tags, 'sigma')) {
    const powerbi = has(tags, 'powerbi') && !has(tags, 'tableau');
    const tableau = has(tags, 'tableau') && !has(tags, 'powerbi');
    if (powerbi || tableau) {
      const source = powerbi ? 'Power BI' : 'Tableau';
      return {
        title: `Planning a ${source} to Sigma migration?`,
        body: 'Start with one representative report. Review its calculations, data dependencies, and acceptance checks before scoping the rebuild.',
        booking: 'Discuss your migration',
        links: [{ href: powerbi ? '/services/power-bi-to-sigma' : '/services/tableau-to-sigma', label: `Explore ${source} migration services` }],
      };
    }
    if (has(tags, 'migration')) {
      return {
        title: 'Planning your Sigma migration?',
        body: 'Start with one representative report. Review its calculations, data dependencies, and acceptance checks before scoping the rebuild.',
        booking: 'Discuss your migration',
        links: [
          { href: '/services/tableau-to-sigma', label: 'Tableau to Sigma migration' },
          { href: '/services/power-bi-to-sigma', label: 'Power BI to Sigma migration' },
        ],
      };
    }
    return {
      title: 'Need help building this in Sigma?',
      body: 'Bring a workbook or reporting question. We can review the data, calculations, and workflow with you.',
      booking: 'Discuss your Sigma workbook',
      links: [{ href: '/services/sigma-consulting', label: 'Explore Sigma consulting' }],
    };
  }
  if (has(tags, 'snowflake')) {
    return {
      title: 'Need help with your Snowflake setup?',
      body: 'Bring the query, connection, or reporting problem you need to solve. We can review the setup and next steps.',
      booking: 'Discuss your Snowflake setup',
      links: [{ href: '/services/snowflake-consulting', label: 'Explore Snowflake consulting' }],
    };
  }
  return {
    title: 'Need help with your reporting?',
    body: 'Bring a reporting question and the decision it needs to support.',
    booking: 'Discuss your reporting problem',
    links: [{ href: '/call-expectations', label: 'See what a first call is like' }],
  };
}
